import { ReactNode, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { getViewOnlyCookie, useLogoutSync } from '@crossnokaye/ux-common';

import { AUTH_SCOPE, AUTH_SCOPE_READ_ONLY } from '@/constants';
import { useAppConfig } from '@/hooks/use-app-config';
import { useLoginAndReturn } from '@/hooks/use-login-and-return';
import { useViewOnlyState } from '@/hooks/use-view-only-state';

import { AuthContext, AuthContextValue } from './types';

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const { data: appConfig } = useAppConfig();

  // Login redirect that returns the user to the page they were on.
  const loginAndReturn = useLoginAndReturn();

  const { isViewOnly, requestViewOnlyToken, requestActiveToken } = useViewOnlyState(
    isAuthenticated,
    getAccessTokenSilently,
    loginAndReturn
  );

  const clientId = appConfig?.auth0?.clientId;

  // Set up logout synchronization across apps and tabs
  const { broadcastLogout } = useLogoutSync({
    clientId,
    isAuthenticated,
    isLoading,
    onLogoutDetected: () => {
      window.location.reload();
    },
  });

  const login = useCallback(() => loginAndReturn({ prompt: 'login' }), [loginAndReturn]);

  const logout = useCallback(() => {
    // Broadcast logout event for instant tab synchronization
    broadcastLogout();

    // Perform normal logout with redirect
    // Auth0 will automatically remove the auth0.is.authenticated cookie
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout, broadcastLogout]);

  // LOAD-BEARING: the single getter every SDK client mints through (client/atlas.ts, client/gaia.ts).
  // The provider defaults to read-only (see auth-provider.tsx for why), so write is opted into HERE,
  // only when active. Read the cookie (synchronous source of truth — the React `isViewOnly` state
  // can lag a render) so active vs. view-only stay on distinct dedup keys, i.e. no coalescing.
  // INVARIANT (ESLint-enforced): never mint via getAccessTokenSilently elsewhere — it bypasses this.
  const getAccessToken = useCallback(async () => {
    if (getViewOnlyCookie()) {
      // Belt-and-suspenders: the read-only default already yields wrt:false; the custom `scopes`
      // prop forces it at the Auth0 Action too. Custom prop, so the dedup key / cache slot is
      // unchanged.
      return await getAccessTokenSilently({
        authorizationParams: { scopes: AUTH_SCOPE_READ_ONLY },
      });
    }
    return await getAccessTokenSilently({ authorizationParams: { scope: AUTH_SCOPE } });
  }, [getAccessTokenSilently]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error,
      user: user || undefined,
      login,
      logout,
      getAccessToken,
      isViewOnly,
      requestViewOnly: requestViewOnlyToken,
      requestActive: requestActiveToken,
    }),
    [
      isLoading,
      isAuthenticated,
      error,
      user,
      login,
      logout,
      getAccessToken,
      isViewOnly,
      requestViewOnlyToken,
      requestActiveToken,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
